import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Upload, X, ArrowUpRight, Loader2, Calculator } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHead from "@/components/PageHead";
import { useT } from "@/context/ContentContext";

const WORK_TYPES = ["Remodelação completa", "Cozinha", "Casa de banho", "Pintura", "Pavimento", "Pladur", "Canalização", "Eletricidade", "Carpintaria", "Reparações", "Outro"];
const PROPERTY_TYPES = ["Apartamento", "Moradia", "Loja / Escritório", "Outro"];
const TIMINGS = ["Assim que possível", "Dentro de 1 mês", "1–3 meses", "3–6 meses", "Ainda estou a planear"];
const BUDGETS = ["Até 2.500 €", "2.500 € – 5.000 €", "5.000 € – 10.000 €", "10.000 € – 25.000 €", "25.000 € – 50.000 €", "Mais de 50.000 €", "Prefiro não indicar"];

export default function Orcamento() {
    const t = useT();
    const [form, setForm] = useState({
        name: "", phone: "", email: "", location: "", property_type: "Apartamento",
        work_types: [], description: "", timing: "", budget: "", rgpd_consent: false,
    });
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
    const toggleWork = (w) => setForm((s) => ({ ...s, work_types: s.work_types.includes(w) ? s.work_types.filter(x => x !== w) : [...s.work_types, w] }));

    const uploadFiles = async (fileList) => {
        setUploading(true);
        const uploaded = [];
        for (const f of Array.from(fileList)) {
            const fd = new FormData();
            fd.append("file", f);
            try {
                const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                uploaded.push({ id: data.id, name: f.name, type: f.type });
            } catch (e) {
                toast.error(`Falha ao enviar ${f.name}`);
            }
        }
        setFiles((s) => [...s, ...uploaded]);
        setUploading(false);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone || !form.email || !form.location || form.work_types.length === 0 || !form.description) {
            toast.error("Preencha os campos obrigatórios");
            return;
        }
        if (!form.rgpd_consent) {
            toast.error("É necessário aceitar a política de privacidade");
            return;
        }
        setSending(true);
        try {
            await api.post("/leads", { ...form, file_ids: files.map(f => f.id) });
            setDone(true);
            window.scrollTo(0, 0);
        } catch (e) {
            toast.error("Erro ao enviar o pedido. Tente novamente.");
        } finally {
            setSending(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center py-20 px-4">
                <div className="max-w-lg text-center card-stone p-10" data-testid="orcamento-success">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[hsl(var(--moss))] flex items-center justify-center">
                        <CheckCircle2 className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 mb-4">Pedido recebido</h1>
                    <p className="text-lg text-stone-600 mb-8">
                        Obrigado pelo contacto. Vamos analisar a informação enviada e entraremos em contacto assim que possível.
                    </p>
                    <p className="text-base text-stone-600 -mt-4 mb-8" data-testid="orcamento-success-client-area">
                        Quando começarmos a preparar a sua proposta, recebe por email o acesso à sua Área do Cliente.
                    </p>
                    <Link to="/" className="btn-moss">
                        Voltar ao início <span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            <PageHead
                title="Pedir orçamento — O Homem da Obra"
                description="Peça um orçamento sem compromisso para a sua obra. Descreva o trabalho, envie fotografias e receba proposta clara."
                canonical="https://ohomemdaobra.pt/orcamento"
            />
            <section className="py-14 sm:py-20">
                <div className="max-w-4xl mx-auto px-6 sm:px-10">
                    <div className="eyebrow mb-6"><span className="logo-mark text-sm text-white">H<span className="dot !text-[hsl(var(--copper))]">.</span></span> Pedido</div>
                    <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold text-stone-900 tracking-tight leading-[1.02]" data-testid="orcamento-title">
                        {t("orcamento.title", "Conte-nos sobre a sua obra.")}
                    </h1>
                    <p className="text-stone-600 text-lg mt-6 max-w-2xl" data-testid="orcamento-subtitle">{t("orcamento.subtitle", "Preencha os dados abaixo e entraremos em contacto para os próximos passos.")}</p>
                </div>
            </section>

            <section className="pb-20">
                <form onSubmit={submit} className="max-w-3xl mx-auto px-6 sm:px-10 bg-white rounded-3xl p-6 sm:p-10 border border-[hsl(var(--border))] space-y-6" data-testid="orcamento-form">
                    <div className="grid sm:grid-cols-2 gap-5">
                        <Field label="Nome *" testid="field-name"><input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} /></Field>
                        <Field label="Telefone *" testid="field-phone"><input required type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inp} /></Field>
                        <Field label="Email *" testid="field-email"><input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inp} /></Field>
                        <Field label="Localidade / Código Postal *" testid="field-location"><input required value={form.location} onChange={(e) => set("location", e.target.value)} className={inp} placeholder="Ex.: Oeiras, 2780-000" /></Field>
                    </div>

                    <Field label="Tipo de imóvel *">
                        <select value={form.property_type} onChange={(e) => set("property_type", e.target.value)} className={inp} data-testid="field-property-type">
                            {PROPERTY_TYPES.map((p) => <option key={p}>{p}</option>)}
                        </select>
                    </Field>

                    <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">Tipo de trabalho *</label>
                        <div className="flex flex-wrap gap-2" data-testid="field-work-types">
                            {WORK_TYPES.map((w) => (
                                <button type="button" key={w} onClick={() => toggleWork(w)} data-testid={`work-${w.replace(/\s+/g, '-').toLowerCase()}`}
                                    className={`px-4 py-2 rounded-full text-sm font-medium border transition ${form.work_types.includes(w) ? "bg-[hsl(var(--moss))] text-white border-[hsl(var(--moss))]" : "bg-[hsl(var(--stone-card))] text-stone-700 border-transparent hover:border-[hsl(var(--moss))]"}`}>
                                    {w}
                                </button>
                            ))}
                        </div>
                    </div>

                    <Field label="Descrição do trabalho *">
                        <textarea required rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} className={inp} placeholder="Conte-nos brevemente o que pretende fazer…" data-testid="field-description" />
                    </Field>

                    <div className="grid sm:grid-cols-2 gap-5">
                        <Field label="Quando pretende realizar a obra?">
                            <select value={form.timing} onChange={(e) => set("timing", e.target.value)} className={inp} data-testid="field-timing">
                                <option value="">Selecionar…</option>
                                {TIMINGS.map((t) => <option key={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Orçamento aproximado (opcional)">
                            <select value={form.budget} onChange={(e) => set("budget", e.target.value)} className={inp} data-testid="field-budget">
                                <option value="">Selecionar…</option>
                                {BUDGETS.map((b) => <option key={b}>{b}</option>)}
                            </select>
                        </Field>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">Fotografias / plantas (opcional)</label>
                        <label className="flex flex-col items-center justify-center border-2 border-dashed border-[hsl(var(--border))] rounded-2xl p-8 cursor-pointer hover:border-[hsl(var(--moss))] transition bg-[hsl(var(--stone-bg))]" data-testid="field-upload">
                            <Upload className="w-6 h-6 text-stone-400 mb-2" />
                            <span className="text-sm text-stone-600">Clique para carregar fotografias ou PDFs (máx 15MB/ficheiro)</span>
                            <input type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadFiles(e.target.files)} />
                        </label>
                        {uploading && <div className="mt-2 text-sm text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> A carregar…</div>}
                        {files.length > 0 && (
                            <div className="mt-3 space-y-1">
                                {files.map((f, i) => (
                                    <div key={f.id} className="flex items-center justify-between bg-stone-50 rounded-lg px-3 py-2 text-sm">
                                        <span className="truncate">{f.name}</span>
                                        <button type="button" onClick={() => setFiles(files.filter((_, x) => x !== i))} className="text-stone-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <label className="flex items-start gap-3 text-sm text-stone-600" data-testid="field-rgpd">
                        <input type="checkbox" checked={form.rgpd_consent} onChange={(e) => set("rgpd_consent", e.target.checked)} className="mt-1 w-4 h-4 accent-[hsl(var(--moss))]" />
                        <span>Aceito que os meus dados sejam tratados para responder a este pedido, conforme a <Link to="/privacidade" className="text-[hsl(var(--moss))] underline">Política de Privacidade</Link>.</span>
                    </label>

                    <button type="submit" disabled={sending} data-testid="orcamento-submit" className="btn-moss w-full justify-center disabled:opacity-50">
                        {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> A enviar…</> : <>Enviar pedido <span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span></>}
                    </button>
                </form>
            </section>
        </>
    );
}

const inp = "w-full px-4 py-3 rounded-2xl border border-[hsl(var(--border))] bg-white text-stone-900 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--moss))] focus:border-transparent";
function Field({ label, children, testid }) {
    return (
        <label className="block" data-testid={testid}>
            <span className="block text-sm font-medium text-stone-700 mb-2">{label}</span>
            {children}
        </label>
    );
}
